import React, { useCallback, useMemo } from 'react';
import { 
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Avatar,
  Chip,
  alpha
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  purple: '#6c5ce7',
  dark: '#2d3436'
};

// Paleta de colores para el editor
const TEXT_COLORS = [
  '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#ffffff',
  '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff',
  '#0000ff', '#9900ff', '#ff00ff',
  '#00b894', '#0984e3', '#6c5ce7', '#fdcb6e', '#e17055', '#d63031'
];

const BACKGROUND_COLORS = [
  'transparent', '#ffffff', '#f8f9fa', '#e9ecef',
  '#ffebee', '#fff3e0', '#fffde7', '#e8f5e9', '#e3f2fd', '#ede7f6'
];

const LessonCreatorAuto = ({ lessons = [], onChange }) => {
  // Módulos del editor mejorados
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': TEXT_COLORS }, { 'background': BACKGROUND_COLORS }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['blockquote', 'code-block'],
      ['clean']
    ]
  }), []);

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'align',
    'link', 'image', 'blockquote', 'code-block'
  ];

  const handleContentChange = useCallback((index, content) => {
    const lessonsCopy = [...lessons];
    lessonsCopy[index] = {
      ...lessonsCopy[index],
      content: content
    };
    onChange(lessonsCopy);
  }, [lessons, onChange]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 14px ${alpha(COLORS.purple, 0.3)}`
          }}
        >
          <MenuBookIcon sx={{ color: 'white', fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
            Lecciones del Curso
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 16, color: COLORS.purple }} />
            <Typography variant="body2" sx={{ color: '#636e72' }}>
              Generadas automáticamente • {lessons.length} lecciones
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Lista de lecciones */}
      {lessons.map((lesson, index) => (
        <Paper 
          key={index} 
          elevation={0} 
          sx={{ 
            mb: 2,
            borderRadius: 2,
            border: '2px solid #e0e0e0',
            overflow: 'hidden',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: alpha(COLORS.primary, 0.3),
              boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.1)}`
            }
          }}
        >
          <Accordion 
            defaultExpanded={false}
            elevation={0}
            sx={{
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                minHeight: 64
              }
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon sx={{ color: COLORS.primary }} />}
              sx={{
                '&:hover': { 
                  backgroundColor: alpha(COLORS.primary, 0.04)
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: alpha(COLORS.primary, 0.15),
                    color: COLORS.primary,
                    fontWeight: 700,
                    fontSize: '0.85rem'
                  }}
                >
                  {index + 1}
                </Avatar>
                <Typography 
                  sx={{ 
                    fontWeight: 600,
                    color: COLORS.dark
                  }}
                >
                  {lesson.title}
                </Typography>
                <Chip
                  icon={<AutoAwesomeIcon />}
                  label="IA"
                  size="small"
                  sx={{
                    bgcolor: alpha(COLORS.purple, 0.1),
                    color: COLORS.purple,
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: COLORS.purple, fontSize: 14 }
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box 
                sx={{ 
                  '& .quill': {
                    border: 'none'
                  },
                  '& .ql-toolbar': {
                    borderTop: '1px solid #e0e0e0',
                    borderBottom: '1px solid #e0e0e0',
                    borderLeft: 'none',
                    borderRight: 'none',
                    bgcolor: '#f8f9fa',
                    '& button:hover': {
                      color: COLORS.primary
                    },
                    '& .ql-active': {
                      color: `${COLORS.primary} !important`
                    }
                  },
                  '& .ql-container': {
                    border: 'none',
                    minHeight: 250,
                    fontSize: '1rem'
                  },
                  '& .ql-editor': {
                    minHeight: 250,
                    padding: '20px',
                    lineHeight: 1.8,
                    '& h1, & h2, & h3': {
                      color: COLORS.dark,
                      fontWeight: 700,
                      marginBottom: '12px'
                    },
                    '& p': {
                      marginBottom: '12px'
                    },
                    '& ul, & ol': {
                      paddingLeft: '24px',
                      marginBottom: '12px'
                    },
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      margin: '12px 0'
                    },
                    '& blockquote': {
                      borderLeft: `4px solid ${COLORS.primary}`,
                      paddingLeft: '16px',
                      margin: '16px 0',
                      color: '#636e72',
                      fontStyle: 'italic'
                    },
                    '& pre': {
                      backgroundColor: COLORS.dark,
                      color: '#dfe6e9',
                      padding: '16px',
                      borderRadius: '8px',
                      overflow: 'auto'
                    },
                    '& a': {
                      color: COLORS.primary,
                      textDecoration: 'underline'
                    }
                  }
                }}
              >
                <ReactQuill
                  key={`editor-${index}`}
                  defaultValue={lesson.content}
                  onChange={(content) => handleContentChange(index, content)}
                  modules={modules}
                  formats={formats}
                  preserveWhitespace
                  placeholder="Contenido de la lección..."
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>
      ))}

      {/* Mensaje si no hay lecciones */}
      {lessons.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            border: '2px dashed #e0e0e0',
            bgcolor: alpha(COLORS.primary, 0.02)
          }}
        >
          <MenuBookIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
          <Typography sx={{ color: '#636e72' }}>
            No hay lecciones generadas todavía
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default React.memo(LessonCreatorAuto);